import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { invalidateMany } from './queryUtils';
import {
  getExtraCosts,
  getExtraCostById,
  createExtraCost,
  updateExtraCost,
  deleteExtraCost,
  uploadExtraCostFile,
  getExtraCostOverview,
  getMyExtraCosts,
  approveExtraCosts,
  type ExtraCostFilters,
  type CreateExtraCostInput,
  type UpdateExtraCostInput,
} from '../services/api/employeeExtraCosts';

/**
 * Invalidate the extra-cost list queues every mutation hook needs to refresh —
 * the admin-wide list (`['employeeExtraCosts']`), the overview
 * (`['employeeExtraCostOverview']`), the per-user list (`['myExtraCosts']`),
 * and the downstream invoice queues (`['employeeInvoices']`,
 * `['employeeInvoiceDetail']`). Mirrors the `invalidateTimeOffLists` and
 * `invalidateExpenseSheetLists` helpers in `useTimeOff.ts` / `useExpenses.ts`.
 * Per-item (`['employeeExtraCost', id]`) invalidations remain inline at the
 * call sites because they only apply to a subset of mutations.
 */
function invalidateExtraCostLists(queryClient: QueryClient): void {
  invalidateMany(queryClient, [
    ['employeeExtraCosts'],
    ['employeeExtraCostOverview'],
    ['myExtraCosts'],
    ['employeeInvoices'],
    ['employeeInvoiceDetail'],
  ]);
}

export function useExtraCosts(filters?: ExtraCostFilters) {
  return useQuery({
    queryKey: ['employeeExtraCosts', filters],
    queryFn: () => getExtraCosts(filters),
  });
}

export function useExtraCost(id: number | undefined) {
  return useQuery({
    queryKey: ['employeeExtraCost', id],
    queryFn: () => getExtraCostById(id!),
    enabled: !!id,
  });
}

export function useMyExtraCosts() {
  return useQuery({
    queryKey: ['myExtraCosts'],
    queryFn: getMyExtraCosts,
  });
}

export function useExtraCostOverview() {
  return useQuery({
    queryKey: ['employeeExtraCostOverview'],
    queryFn: getExtraCostOverview,
  });
}

export function useCreateExtraCost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExtraCostInput) => createExtraCost(input),
    onSuccess: () => {
      invalidateExtraCostLists(queryClient);
    },
  });
}

export function useUpdateExtraCost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateExtraCostInput }) =>
      updateExtraCost(id, input),
    onSuccess: (_data, { id }) => {
      invalidateExtraCostLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['employeeExtraCost', id] });
    },
  });
}

export function useDeleteExtraCost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteExtraCost(id),
    onSuccess: () => {
      invalidateExtraCostLists(queryClient);
    },
  });
}

export function useApproveExtraCosts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => approveExtraCosts(ids),
    onSuccess: () => {
      invalidateExtraCostLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['employeeExtraCost'] });
    },
  });
}

export function useUploadExtraCostFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) => uploadExtraCostFile(id, file),
    onSuccess: (_data, { id }) => {
      invalidateExtraCostLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['employeeExtraCost', id] });
    },
  });
}
